import { AppNavItem, AppNavLink, Css, Icon, Tokens } from "@homebound/beam";
import { ReactNode } from "react";
import { matchPath, Outlet, useLocation } from "react-router-dom";
import { BeamSideNavViewportLayout } from "src/components/layout/BeamSideNavViewportLayout";
import { useFeatureFlags } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import { useScrollRestore } from "src/hooks/useScrollRestore";
import { useDevelopmentContext } from "src/routes/developments/context/DevelopmentContext";
import {
  createDevelopmentChangeLogUrl,
  createDevelopmentDropsUrl,
  createDevelopmentJobLogsUrl,
  createDevelopmentLotSchedulesUrl,
  createDevelopmentLotSummaryUrl,
  createDevelopmentPlanAndOptionsUrl,
  createDevelopmentProcurementUrl,
  createDevelopmentPurchaseOrdersUrl,
  createDevelopmentScheduleUrl,
  createDevelopmentScopeTemplatesUrl,
  createDevelopmentSettingsUrl,
  createDevelopmentTaskLookaheadReportUrl,
  createDevelopmentTeamMembersUrl,
  createDocumentsUrl,
  createProductOfferingsUrl,
} from "src/RouteUrls";
import { BeamSideNavLayout } from "src/routing/components/layouts/BeamSideNavLayout";
import { developmentPaths } from "src/routing/domains/developmentsPaths";

/**
 * Two chrome wrappers for development detail pages, coexisting while we migrate to Beam's layouts.
 *
 * Both bind the same nav definition (`useDevelopmentSideNav`), so items, top, rail width, and
 * contrast are declared once below and the rail looks identical on either branch. They differ only
 * in the page chrome wrapped around the `<Outlet />`, which is why a page can move between them
 * without touching the nav.
 *
 * `DevelopmentBeamSideNavLayout` — Beam's `SideNavLayout`: document scroll, sticky rail, no outer
 * scroll frame. Use it when the page owns Beam page chrome (`PageHeaderLayout`, `GridTableLayout`,
 * `useRightPane`) and sits under `BeamNavbarLayout`. This is the destination for every development
 * page.
 *
 * `DevelopmentLegacySideNavLayout` — `BeamSideNavViewportLayout`: Beam's `SideNav` inside the old
 * rail, with a nested `ScrollableParent`. Use it for pages still on legacy `PageHeader` chrome,
 * which assume a scroll frame from the layout rather than the document.
 *
 * Routes are split accordingly in `src/routing/domains/developments/detail/developmentDetail.tsx`.
 */
export function DevelopmentBeamSideNavLayout() {
  const { sideNav, railWidthPx } = useDevelopmentSideNav();

  return <BeamSideNavLayout contrastRail sideNav={sideNav} railWidthPx={railWidthPx} />;
}

export function DevelopmentLegacySideNavLayout() {
  const { sideNav, railWidthPx } = useDevelopmentSideNav();

  return (
    <BeamSideNavViewportLayout contrastRail sideNav={sideNav} railWidthPx={railWidthPx}>
      <Outlet />
    </BeamSideNavViewportLayout>
  );
}

export function useDevelopmentSideNav() {
  const development = useDevelopmentContext();
  const { pathname } = useLocation();
  const { featureIsEnabled } = useFeatureFlags();
  // Temporary logic for scroll restoring while we migrate pages to Beam's SideNavLayout.
  // Documents has been migrated to Beam's SideNavLayout, which is different than the other
  // development pages (BeamSideNavViewportLayout), so the side nav remounts between navigations.
  useScrollRestore(`developmentSideNav:${development.id}`, '[data-testid="sideNav_items"]');
  const { top, items, railWidthPx } = buildDevelopmentSideNavProps(development, { pathname, featureIsEnabled });

  return { sideNav: { top, items }, railWidthPx };
}

type BuildDevelopmentSideNavOptions = { pathname: string; featureIsEnabled: (flag: FeatureFlagType) => boolean };

type DevelopmentNavContext = { id?: string; name?: string; lotsAcquired?: number };

type DevelopmentSideNavProps = { top?: ReactNode; items: AppNavItem[]; railWidthPx: number; contrastRail: boolean };

export function buildDevelopmentSideNavProps(
  development: DevelopmentNavContext,
  options: BuildDevelopmentSideNavOptions,
): DevelopmentSideNavProps {
  const { id, name, lotsAcquired } = development;
  const { pathname, featureIsEnabled } = options;

  if (!id) {
    return { items: [], railWidthPx: 220, contrastRail: true };
  }

  const developmentLinks: AppNavLink[] = [
    {
      label: "Overview",
      onClick: createDevelopmentLotSummaryUrl(id),
      active: isNavActive(pathname, `${developmentPaths.lotSummary}/*`),
    },
    { label: "Documents", onClick: createDocumentsUrl(id), active: isNavActive(pathname, developmentPaths.documents) },
    {
      label: "Team Members",
      onClick: createDevelopmentTeamMembersUrl(id),
      active: isNavActive(pathname, developmentPaths.team),
    },
    ...(featureIsEnabled(FeatureFlagType.ProductConfig)
      ? [
          {
            label: "Drops",
            onClick: createDevelopmentDropsUrl(id),
            active: isNavActive(pathname, developmentPaths.drops),
          },
        ]
      : []),
    {
      label: "Job Logs",
      onClick: createDevelopmentJobLogsUrl(id),
      active: isNavActive(pathname, `${developmentPaths.jobLogs}/*`),
    },
  ];

  const scheduleLinks: AppNavLink[] = [
    {
      label: "Development Schedule",
      onClick: createDevelopmentScheduleUrl(id),
      active: isNavActive(pathname, developmentPaths.developmentSchedule),
    },
    {
      label: "Lot Schedules",
      onClick: createDevelopmentLotSchedulesUrl(id),
      active: isNavActive(pathname, developmentPaths.lotSchedules),
    },
    {
      label: "Task Lookahead",
      onClick: createDevelopmentTaskLookaheadReportUrl(id),
      active: isNavActive(pathname, developmentPaths.taskLookaheadReport),
    },
  ];

  const scopeLinks: AppNavLink[] = [
    {
      label: "Plan & Options",
      onClick: createDevelopmentPlanAndOptionsUrl(id),
      active: isNavActive(pathname, developmentPaths.planAndOptions),
    },
    ...(featureIsEnabled(FeatureFlagType.ProductConfig)
      ? [
          {
            label: "Product Offerings",
            onClick: createProductOfferingsUrl(id),
            active: isNavActive(pathname, developmentPaths.productOfferings),
          },
        ]
      : []),
    {
      label: "Templates",
      onClick: createDevelopmentScopeTemplatesUrl(id),
      active: isNavActive(pathname, developmentPaths.scopeTemplates),
    },
    ...(featureIsEnabled(FeatureFlagType.ChangeRequestMvp)
      ? [
          {
            label: "Change Log",
            onClick: createDevelopmentChangeLogUrl(id),
            active: isNavActive(pathname, `${developmentPaths.changeLog}/*`),
          },
        ]
      : []),
  ];

  const tradeLinks: AppNavLink[] = [
    {
      label: "Contracts",
      onClick: createDevelopmentProcurementUrl(id),
      active: isNavActive(pathname, developmentPaths.procurement),
    },
    {
      label: "Purchase Orders",
      onClick: createDevelopmentPurchaseOrdersUrl(id),
      active: isNavActive(pathname, developmentPaths.purchaseOrders),
    },
  ];

  const items: AppNavItem[] = [
    { section: true, label: "Development", items: developmentLinks },
    { section: true, label: "Schedules", items: scheduleLinks },
    { section: true, label: "Scope", items: scopeLinks },
    { section: true, label: "Trades", items: tradeLinks },
    ...(featureIsEnabled(FeatureFlagType.DynamicSchedules)
      ? [
          {
            section: true as const,
            items: [
              {
                label: "Settings",
                onClick: createDevelopmentSettingsUrl(id),
                active: isNavActive(pathname, developmentPaths.settings),
              },
            ],
          },
        ]
      : []),
  ];

  return {
    items,
    railWidthPx: 220,
    contrastRail: true,
    top: (
      <div>
        <div css={Css.df.jcc.aic.br8.wPx(48).hPx(48).bgColor(Tokens.SurfaceRaised).color(Tokens.OnSurface).$}>
          <Icon icon="buildingHouse" />
        </div>
        {name && (
          <>
            <h1 css={Css.lg.mt1.color(Tokens.OnSurface).$}>{name}</h1>
            <span css={Css.xs.color(Tokens.OnSurfaceMuted).$}>{lotsAcquired ?? 0} Lots Acquired</span>
          </>
        )}
      </div>
    ),
  };
}

function isNavActive(pathname: string, path?: string | string[]): boolean {
  if (!path) return false;
  const paths = Array.isArray(path) ? path : [path];
  return paths.some((p) => !!matchPath({ path: p, end: false }, pathname));
}
